import { SignedIn, SignedOut, UserButton } from '@daveyplate/better-auth-ui';
import { Link, useRouter } from '@tanstack/react-router';
import { ChevronDown, LayoutDashboard, Menu, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useSession } from '@/hooks/authHooks';
import { track } from '@/lib/mixpanel';
import { isAdminRole } from '@/utils/auth';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const router = useRouter();
  const location = router.state.location.pathname;
  const { user } = useSession();
  const isAdmin = isAdminRole(user?.role);

  const isActive = (path: string) => location === path;

  // Track scroll position to shrink/expand navbar
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);
    };

    // Check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // CSS for the hover underline animation
  const navLinkStyle = `
    .nav-link {
      position: relative;
    }
    .nav-link:after {
      content: '';
      position: absolute;
      width: 0;
      height: 2px;
      bottom: 0;
      left: 0;
      background-color: hsl(var(--primary));
      transition: width 0.3s ease;
    }
    .nav-link:hover:after {
      width: 100%;
    }
    .active-link:after {
      content: '';
      position: absolute;
      width: 100%;
      height: 2px;
      bottom: 0;
      left: 0;
      background-color: hsl(var(--primary));
    }
  `;

  return (
    <>
      <style>{navLinkStyle}</style>
      <header className="fixed left-0 right-0 top-0 z-50 px-4 py-4">
        <nav
          className={`mx-auto transition-all duration-500 ease-out ${
            isScrolled ? 'max-w-3xl' : 'max-w-6xl'
          }`}>
          <div
            className={`flex h-14 items-center justify-between rounded-full border bg-white/80 backdrop-blur-md transition-all duration-500 ease-out ${
              isScrolled
                ? 'border-neutral-300 px-4 shadow-md'
                : 'border-neutral-200 px-6 shadow-sm'
            }`}>
            {/* Logo */}
            <Link
              to="/"
              onClick={() => track('NavbarClick', { destination: 'Home' })}
              className="flex-shrink-0">
              <span
                className={`px-2 font-logo text-2xl font-medium leading-relaxed tracking-tight text-primary transition-all duration-500`}>
                SolomonQ
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div
              className={`hidden items-center transition-all duration-500 md:flex ${
                isScrolled ? 'gap-x-0' : 'gap-x-2'
              }`}>
              <Link
                to="/"
                onClick={() => track('NavbarClick', { destination: 'Home' })}>
                <span
                  className={`nav-link mr-1 whitespace-nowrap py-2 text-sm font-medium transition-all duration-500 ${
                    isScrolled ? 'px-2' : 'px-3'
                  } ${
                    isActive('/')
                      ? 'active-link text-primary'
                      : 'text-neutral-600 hover:text-primary'
                  }`}>
                  Acasă
                </span>
              </Link>

              <Link
                to="/create-alert"
                onClick={() =>
                  track('NavbarClick', { destination: 'Create Alert' })
                }>
                <span
                  className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border py-1.5 text-sm font-medium transition-all duration-500 ${
                    isScrolled ? 'px-2.5' : 'px-3'
                  } ${
                    isActive('/create-alert')
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-neutral-400 bg-neutral-100 text-neutral-800 hover:border-primary hover:bg-primary hover:text-primary-foreground'
                  }`}>
                  <Sparkles
                    className={`h-3.5 w-3.5 transition-all duration-500`}
                  />
                  Alertă AI
                </span>
              </Link>

              <Link
                to="/prompt-runs"
                onClick={() =>
                  track('NavbarClick', { destination: 'Prompt Runs' })
                }>
                <span
                  className={`nav-link whitespace-nowrap py-2 text-sm font-medium transition-all duration-500 ${
                    isScrolled ? 'px-2' : 'px-3'
                  } ${
                    isActive('/prompt-runs')
                      ? 'active-link text-primary'
                      : 'text-neutral-600 hover:text-primary'
                  }`}>
                  Istoric AI
                </span>
              </Link>

              <Link
                to="/graphs"
                onClick={() => track('NavbarClick', { destination: 'Graphs' })}>
                <span
                  className={`nav-link whitespace-nowrap py-2 text-sm font-medium transition-all duration-500 ${
                    isScrolled ? 'px-2' : 'px-3'
                  } ${
                    isActive('/graphs')
                      ? 'active-link text-primary'
                      : 'text-neutral-600 hover:text-primary'
                  }`}>
                  Grafice
                </span>
              </Link>

              <Link
                to="/reports"
                onClick={() =>
                  track('NavbarClick', { destination: 'Reports' })
                }>
                <span
                  className={`nav-link whitespace-nowrap py-2 text-sm font-medium transition-all duration-500 ${
                    isScrolled ? 'px-2' : 'px-3'
                  } ${
                    isActive('/reports')
                      ? 'active-link text-primary'
                      : 'text-neutral-600 hover:text-primary'
                  }`}>
                  Raportări
                </span>
              </Link>

              {/* More dropdown */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`nav-link flex items-center gap-1 whitespace-nowrap py-2 text-sm font-medium text-neutral-600 transition-all duration-500 hover:text-primary focus:outline-none focus-visible:outline-none ${
                      isScrolled ? 'px-2' : 'px-3'
                    }`}>
                    Mai mult
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-48 rounded-lg border border-neutral-200 bg-white/95 shadow-lg backdrop-blur-md">
                  <DropdownMenuItem asChild>
                    <Link
                      to="/terms"
                      onClick={() =>
                        track('NavbarClick', {
                          destination: 'Terms and Conditions',
                        })
                      }
                      className="cursor-pointer rounded px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100">
                      Termeni și Condiții
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/privacy-policy"
                      onClick={() =>
                        track('NavbarClick', {
                          destination: 'Privacy Policy',
                        })
                      }
                      className="cursor-pointer rounded px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100">
                      Politică confidențialitate
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/contact"
                      onClick={() =>
                        track('NavbarClick', {
                          destination: 'Contact',
                        })
                      }
                      className="cursor-pointer rounded px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100">
                      Contact
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      href="https://docs.google.com/forms/d/e/1FAIpQLSe8FYp4NzLHZqDMjSbqQAabSBTCK0wWSdcvodPPjhzHPmvn6Q/viewform"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        track('NavbarClick', {
                          destination: 'External Feedback',
                        })
                      }
                      className="cursor-pointer rounded px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-100">
                      Feedback
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Right side - Auth */}
            <div className="flex items-center gap-3">
              <div className="hidden md:flex md:items-center md:gap-3">
                <SignedIn>
                  <UserButton
                    className="flex h-9 w-9 items-center"
                    size="icon"
                    additionalLinks={
                      isAdmin
                        ? [
                            {
                              href: '/admin',
                              label: 'Admin Dashboard',
                              signedIn: true,
                              icon: <LayoutDashboard className="h-6 w-6" />,
                            },
                          ]
                        : undefined
                    }
                    classNames={{
                      trigger: {
                        base: 'text-sm font-medium',
                        avatar: {
                          fallback:
                            'bg-primary text-primary-foreground w-full h-full flex items-center justify-center',
                        },
                      },
                      content: {
                        user: {
                          avatar: {
                            base: 'w-10 h-10 cursor-pointer',
                            fallback:
                              'bg-primary text-primary-foreground w-full h-full flex items-center justify-center',
                          },
                        },
                      },
                    }}
                  />
                </SignedIn>
                <SignedOut>
                  <Link
                    to="/auth/$authFlow"
                    params={{ authFlow: 'sign-in' }}
                    onClick={() =>
                      track('NavbarClick', {
                        destination: 'Sign In',
                      })
                    }>
                    <span
                      className={`nav-link whitespace-nowrap py-2 text-sm font-medium text-neutral-600 transition-all duration-500 hover:text-primary ${
                        isScrolled ? 'px-2' : 'px-3'
                      }`}>
                      Autentificare
                    </span>
                  </Link>
                </SignedOut>
              </div>

              {/* Mobile menu button */}
              <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="h-5 w-5 text-neutral-700" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="w-[300px] border-l border-neutral-200 bg-white/95 backdrop-blur-md">
                  <div className="flex flex-col gap-6 pt-6">
                    <span className="font-logo text-2xl font-medium leading-relaxed tracking-tight text-primary">
                      SolomonQ
                    </span>

                    <nav className="flex flex-col gap-1">
                      <Link
                        to="/"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'Home (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}>
                        <span
                          className={`block rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive('/')
                              ? 'bg-neutral-100 text-primary'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-primary'
                          }`}>
                          Acasă
                        </span>
                      </Link>

                      <Link
                        to="/create-alert"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'Create Alert (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}>
                        <span
                          className={`flex items-center gap-2 rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive('/create-alert')
                              ? 'bg-primary text-primary-foreground'
                              : 'border border-neutral-300 text-neutral-700 hover:border-primary hover:bg-primary hover:text-primary-foreground'
                          }`}>
                          <Sparkles className="h-4 w-4" />
                          Creează Alertă AI
                        </span>
                      </Link>

                      <Link
                        to="/prompt-runs"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'Prompt Runs (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}>
                        <span
                          className={`block rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive('/prompt-runs')
                              ? 'bg-neutral-100 text-primary'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-primary'
                          }`}>
                          Istoric Rulări AI
                        </span>
                      </Link>

                      <Link
                        to="/graphs"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'Graphs (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}>
                        <span
                          className={`block rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive('/graphs')
                              ? 'bg-neutral-100 text-primary'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-primary'
                          }`}>
                          Grafice
                        </span>
                      </Link>

                      <Link
                        to="/reports"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'Reports (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}>
                        <span
                          className={`block rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive('/reports')
                              ? 'bg-neutral-100 text-primary'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-primary'
                          }`}>
                          Raportări
                        </span>
                      </Link>

                      <Link
                        to="/terms"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'Terms (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}>
                        <span
                          className={`block rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive('/terms')
                              ? 'bg-neutral-100 text-primary'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-primary'
                          }`}>
                          Termeni și Condiții
                        </span>
                      </Link>

                      <Link
                        to="/privacy-policy"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'Privacy Policy (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}>
                        <span
                          className={`block rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive('/privacy-policy')
                              ? 'bg-neutral-100 text-primary'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-primary'
                          }`}>
                          Politică confidențialitate
                        </span>
                      </Link>

                      <Link
                        to="/contact"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'Contact (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}>
                        <span
                          className={`block rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                            isActive('/contact')
                              ? 'bg-neutral-100 text-primary'
                              : 'text-neutral-600 hover:bg-neutral-50 hover:text-primary'
                          }`}>
                          Contact
                        </span>
                      </Link>

                      <a
                        href="https://docs.google.com/forms/d/e/1FAIpQLSe8FYp4NzLHZqDMjSbqQAabSBTCK0wWSdcvodPPjhzHPmvn6Q/viewform"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          track('NavbarClick', {
                            destination: 'External Feedback (Mobile)',
                          });
                          setIsMenuOpen(false);
                        }}
                        className="block rounded-lg px-4 py-3 text-base font-medium text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-primary">
                        Feedback
                      </a>
                    </nav>

                    {/* Mobile Auth */}
                    <div className="border-t border-neutral-200 pt-4">
                      <SignedIn>
                        <Link
                          to="/account/$accountFlow"
                          params={{ accountFlow: 'settings' }}
                          onClick={() => {
                            track('NavbarClick', {
                              destination: 'Account Settings (Mobile)',
                            });
                            setIsMenuOpen(false);
                          }}
                          className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-base font-medium text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-primary">
                          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                            {user?.name?.charAt(0)?.toUpperCase() ||
                              user?.email?.charAt(0)?.toUpperCase() ||
                              'U'}
                          </div>
                          <span>Contul meu</span>
                        </Link>
                        {isAdmin && (
                          <Link
                            to="/admin"
                            onClick={() => {
                              track('NavbarClick', {
                                destination: 'Admin Dashboard (Mobile)',
                              });
                              setIsMenuOpen(false);
                            }}
                            className="flex items-center gap-2 rounded-lg px-4 py-3 text-base font-medium text-neutral-600 transition-colors hover:bg-neutral-50 hover:text-primary">
                            <LayoutDashboard className="h-5 w-5" />
                            Admin Dashboard
                          </Link>
                        )}
                      </SignedIn>
                      <SignedOut>
                        <div className="px-4">
                          <Link
                            to="/auth/$authFlow"
                            params={{ authFlow: 'sign-in' }}
                            onClick={() => {
                              track('NavbarClick', {
                                destination: 'Sign In (Mobile)',
                              });
                              setIsMenuOpen(false);
                            }}>
                            <span className="block rounded-lg border border-neutral-200 px-4 py-3 text-center text-base font-medium text-neutral-700 transition-colors hover:bg-neutral-50">
                              Autentificare
                            </span>
                          </Link>
                        </div>
                      </SignedOut>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}
